/* eslint-disable no-underscore-dangle */
import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { trans } from '@spotahome/soyuz/client';

import FormInput from '../FormInput';

import './FormDate.pikaday.module.scss'; // Do not delete it

import {
  DISPLAYED_DATE_FORMAT,
  formatDisplayDate,
  formatDate,
  parseDate
} from '../utils/dateUtils';

import styles from './FormDate.module.scss';

import getPikaday from './pikaday';

const TRANS_DELIMITER = '_';

export const useFormDate = ({
  id,
  title,
  name,
  bold,
  dataTest,
  dataTestCalendar,
  onChange,
  onCalendarOpen,
  onCalendarClose,
  disabled,
  calendarBordered,
  placeholder,
  absolutePosition,
  value,
  renderCalendarContent,
  required,
  hasError,
  isCalendarOpen: isCalendarOpenProp,
  minDate,
  maxDate,
  disableDayFn,
  closeOnSelect = true,
  showCalendarArrows = true,
  numberOfMonths = 1,
  horizontalCalendar = false,
  startRange = null,
  endRange = null,
  hideOnBlur,
  initiallyOpen = false
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(isCalendarOpenProp);
  const pickerRef = useRef(null);
  const displayRef = useRef(null);
  const triggerRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const mainCalendarContainerRef = useRef(null);
  const rawDate = pickerRef.current ? pickerRef.current.getDate() : value;
  const humanReadableDate = rawDate ? formatDisplayDate(rawDate) : '';

  const currentValue = useRef(value);

  const handleCalendarOpen = useCallback(() => {
    if (disabled || isCalendarOpenProp) {
      return;
    }

    if (!pickerRef.current) {
      return;
    }

    setIsCalendarOpen(true);

    onCalendarOpen();
  }, [disabled, isCalendarOpenProp, onCalendarOpen]);

  const handleCalendarClose = useCallback(() => {
    if (!pickerRef.current) {
      return;
    }

    const formattedDate = pickerRef.current.getDate()
      ? formatDate(pickerRef.current.getDate())
      : '';

    // When pikaday is not bound (closeOnSelect is false),
    // it is force to close as soon as the component is mounted.
    // We need to check if the date is different from the current value
    // to avoid undesired behavior
    if (
      formattedDate &&
      formattedDate !== currentValue.current &&
      !closeOnSelect
    ) {
      currentValue.current = formattedDate;
      onChange({ value: formattedDate, name });
    } else {
      setIsCalendarOpen(false);
      onCalendarClose();
    }
  }, [closeOnSelect, onChange, name, onCalendarClose]);

  const handleDateSelect = useCallback(
    date => {
      const formattedDate = formatDate(date);
      onChange({ value: formattedDate, name });

      if (closeOnSelect) {
        pickerRef.current.hide();
      }
    },
    [closeOnSelect, onChange, name]
  );

  const handleMainContainerClick = useCallback(() => {
    if (pickerRef.current && !pickerRef.current.isVisible()) {
      pickerRef.current.show();
    }
  }, []);

  useEffect(() => {
    const boundPikaday = closeOnSelect;
    const Pikaday = getPikaday();
    pickerRef.current = new Pikaday({
      bound: boundPikaday,
      container: calendarContainerRef.current,
      field: displayRef.current,
      trigger: triggerRef.current,
      firstDay: 1,
      i18n: {
        previousMonth: trans('pikaday.label.previous_month'),
        nextMonth: trans('pikaday.label.next_month'),
        months: trans('pikaday.label.months').split(TRANS_DELIMITER),
        weekdays: trans('pikaday.label.weekdays').split(TRANS_DELIMITER),
        weekdaysShort: trans('pikaday.label.weekdays_short').split(
          TRANS_DELIMITER
        ),
        dateInPlaceholder: trans('pikaday.label.date_in'),
        dateOutPlaceholder: trans('pikaday.label.date_out')
      },
      disableDayFn,
      minDate,
      maxDate,
      onClose: handleCalendarClose,
      onOpen: handleCalendarOpen,
      onSelect: handleDateSelect,
      parse: s => (s ? parseDate(s, DISPLAYED_DATE_FORMAT) : null),
      toString: v => formatDisplayDate(v),
      showArrows: showCalendarArrows,
      numberOfMonths,
      horizontalCalendar,
      hideOnBlur
    });

    // When pikaday is not bound to a field, it will be rendered as soon as the component is mounted.
    if (!boundPikaday && pickerRef.current.isVisible() && !initiallyOpen) {
      pickerRef.current.hide();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update the callbacks to use the new ones, so the state is not hoisted
    pickerRef.current._o.onSelect = handleDateSelect;
    pickerRef.current._o.onClose = handleCalendarClose;
    pickerRef.current._o.onOpen = handleCalendarOpen;
  }, [handleDateSelect, handleCalendarClose, handleCalendarOpen]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (!hideOnBlur) {
        return;
      }

      const pickerElement = pickerRef.current.el;
      const path = event.composedPath && event.composedPath();

      const isClickInsideCalendar = path
        ? path.includes(pickerElement)
        : pickerElement.contains(event.target);

      const isClickInsideInput = path
        ? path.includes(displayRef.current)
        : displayRef.current.contains(event.target);

      if (
        mainCalendarContainerRef.current &&
        !mainCalendarContainerRef.current.contains(event.target) &&
        !isClickInsideCalendar &&
        !isClickInsideInput
      ) {
        pickerRef.current.hide();
      }
    };

    if (!closeOnSelect && pickerRef.current && isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen, closeOnSelect]);

  useEffect(() => {
    setIsCalendarOpen(isCalendarOpenProp);
    if (isCalendarOpenProp) {
      pickerRef.current.show();
    } else {
      pickerRef.current.hide();
    }
  }, [isCalendarOpenProp]);

  useEffect(() => {
    pickerRef.current.setMinDate(minDate);
  }, [minDate]);

  useEffect(() => {
    pickerRef.current.setMaxDate(maxDate);
  }, [maxDate]);

  useEffect(() => {
    if (startRange) {
      pickerRef.current.setStartRange(parseDate(startRange));
    }
  }, [startRange]);

  useEffect(() => {
    if (endRange) {
      pickerRef.current.setEndRange(parseDate(endRange));
    }
  }, [endRange]);

  useEffect(() => {
    if (value) {
      pickerRef.current.setDate(parseDate(value), true);
    } else {
      pickerRef.current.clearStartRange();
      pickerRef.current.clearEndRange();
      pickerRef.current.setDate(null, true);
      pickerRef.current.draw();
    }
  }, [value]);

  const getClassNames = () => ({
    calendarWrapper: classNames(
      styles['formdate__calendar-wrapper'],
      {
        [styles['formdate__calendar-wrapper--absolute']]: absolutePosition,
        [styles['formdate__calendar-wrapper--hidden']]: !isCalendarOpen,
        [styles['formdate__calendar-wrapper--bordered']]: calendarBordered,
        [styles['formdate__calendar-wrapper--infinite']]:
          numberOfMonths > 2 && !horizontalCalendar
      },
      'pika-single' // don't remove: this prevents calendar being closed when clicking elements inside the calendar
    ),
    calendar: classNames(styles.formdate__calendar),
    input: classNames(styles.formdate__input, {
      [styles['formdate__input--disabled']]: disabled,
      [styles['formdate__input--selected']]: isCalendarOpen,
      [styles['formdate__input--not-selected']]: !isCalendarOpen,
      [styles['formdate__input--with-title']]: !!title,
      [styles['formdate__input--with-error']]: hasError
    })
  });

  const cns = getClassNames();

  const renderCalendar = () => (
    <div
      translate="no"
      className={cns.calendarWrapper}
      ref={mainCalendarContainerRef}
    >
      <div data-test={dataTestCalendar} ref={calendarContainerRef} />
      {isCalendarOpen ? renderCalendarContent() : null}
    </div>
  );

  const renderInput = () => (
    <div
      className={cns.input}
      ref={triggerRef}
      onClick={handleCalendarOpen}
      role="button"
      tabIndex={0}
    >
      <FormInput
        id={id}
        dataTest={dataTest}
        placeholder={placeholder}
        title={title}
        readOnly
        bold={bold}
        name={name}
        innerRef={displayRef}
        type="text"
        value={humanReadableDate}
        disabled={disabled}
        required={required}
        hasError={hasError}
      />
    </div>
  );

  return {
    humanReadableDate,
    selectedDate: rawDate ? formatDate(rawDate) : '',
    displayRef,
    triggerRef,
    pickerRef,
    renderInput,
    renderCalendar,
    getClassNames,
    onClick: handleMainContainerClick
  };
};

const noop = () => {};

const FormDate = ({
  id = 'form-date',
  name = 'form-date',
  dataTest = 'form-date',
  placeholder = '',
  onChange = noop,
  onCalendarOpen = noop,
  onCalendarClose = noop,
  disabled = false,
  calendarBordered = false,
  absolutePosition = false,
  bold = false,
  value = '',
  children = null,
  renderCalendarContent = noop,
  required = false,
  hasError = false,
  title = '',
  isCalendarOpen = false,
  calendarPosition = '',
  minDate = '',
  maxDate = '',
  disableDayFn = noop,
  closeOnSelect = true,
  ...restProps
}) => {
  const {
    renderInput,
    renderCalendar,
    getClassNames,
    displayRef,
    triggerRef,
    humanReadableDate,
    selectedDate,
    onClick
  } = useFormDate({
    id,
    name,
    dataTest,
    placeholder,
    onChange,
    onCalendarOpen,
    onCalendarClose,
    disabled,
    calendarBordered,
    absolutePosition,
    bold,
    value,
    children,
    renderCalendarContent,
    required,
    hasError,
    title,
    isCalendarOpen,
    calendarPosition,
    minDate,
    maxDate,
    disableDayFn,
    closeOnSelect,
    ...restProps
  });
  const cns = getClassNames();

  return children ? (
    children({
      renderInput,
      renderCalendar,
      displayRef, // ref where the date will be displayed (any click outside of it will close the calendar)
      triggerRef, // calendar will open when this trigger ref is clicked
      humanReadableDate,
      selectedDate,
      onClick
    })
  ) : (
    <div className={cns.container}>
      {renderInput()}
      {renderCalendar()}
    </div>
  );
};

FormDate.propTypes = {
  id: PropTypes.string,
  onChange: PropTypes.func,
  onCalendarOpen: PropTypes.func,
  onCalendarClose: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  calendarBordered: PropTypes.bool,
  absolutePosition: PropTypes.bool,
  bold: PropTypes.bool,
  value: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.array
  ]),
  name: PropTypes.string,
  dataTest: PropTypes.string,
  renderCalendarContent: PropTypes.func,
  title: PropTypes.string,
  required: PropTypes.bool,
  hasError: PropTypes.bool,
  isCalendarOpen: PropTypes.bool,
  calendarPosition: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  disableDayFn: PropTypes.func,
  closeOnSelect: PropTypes.bool
};

export default FormDate;
